/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { Box, debounce, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "../../components/popups/popup";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ExpenseService from "../../services/expenseService";
import AddExpenseBox from "../../components/boxes/expense/addExpenseBox";
import ViewExpenseModal from "../../components/modals/expense/viewExpenseModal";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import UserDropdown from "../../components/dropdowns/userDropdown";
import ExpenseTypeDropdown from "../../components/dropdowns/expenseTypeDropdown";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UpdateExpenseBox from "../../components/boxes/expense/updateExpenseBox";
import DeleteExpenseModal from "../../components/modals/expense/deleteExpenseModal";
import SearchInput from "../../components/inputs/searchInput";
import OnOffSwitchForDeleted from "../../components/inputs/onOffSwitchForDeleted";
import { formatPrice } from "../../utils";
import TimeDropdown from "../../components/dropdowns/timeDropdown";
import PrintExpenses from "../../components/prints/printExpenses";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import { AuthContext } from "../../provider/authProvider";

const Expense = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [expenses, setExpenses] = useState([]);
    const [selectedExpense, setSelectedExpense] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedExpenseType, setSelectedExpenseType] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalAmount, setTotalAmount] = useState(0);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);
    const [checkFirstRender, setCheckFirstRender] = useState(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (expense) => {
        setSelectedExpense(expense);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        setSelectedUser(null);
        setSelectedExpenseType(null);
        await fetchExpenses();

    };

    const handleOpenUpdate = (expense) => {
        setOpenUpdate(true);
        setSelectedExpense(expense);
    };

    const handleOpenDelete = (expense) => {
        setSelectedExpense(expense);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedUser(null);
            setSelectedExpenseType(null);
            await fetchExpenses();
        }
    };

    const debouncedFetchBranches = useMemo(() =>
        debounce((name, userId, expenseTypeId, selectedBranch) => {
            setPage(1);
            setHasMore(true);
            fetchExpenses(1, name, userId, expenseTypeId, selectedBranch);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchBranches(value, selectedUser?.id, selectedExpenseType?.id, selectedBranch?.id);
    };

    const fetchExpenses = async (page = 1, name = '', userId = null, expenseTypeId = null, branchId = null) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                userId, expenseTypeId, branchId,
                name, deleted,
                ...selectedTime,
                ...sortState
            };
            const data = await ExpenseService.fetch(page, filters);
            const { expense, totalAmount } = data;
            setTotalAmount(totalAmount);
            
            if (expense.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setExpenses([]);
                }
            } else {
                setExpenses((prevs) => (page === 1 ? expense : [...prevs, ...expense]));
            }
        } catch (error) {
            console.error('Error fetching expense:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetFilter = () => {
        setSearchTerm('');
        setSelectedUser(null);
        setHasMore(true);
        setSelectedExpenseType(null);
        setSelectedBranch(null);
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchExpenses(page, searchTerm, selectedUser?.id, selectedExpenseType?.id, selectedBranch?.id);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchExpenses(1, searchTerm, selectedUser?.id, selectedExpenseType?.id, checkFirstRender ? null : selectedBranch?.id);
        setCheckFirstRender(false);
    }, [selectedUser, selectedExpenseType, sortState, deleted, selectedTime, selectedBranch]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchExpenses(page + 1, searchTerm, selectedUser?.id, selectedExpenseType?.id, selectedBranch?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteExpenseModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} expense={selectedExpense} />
                <ViewExpenseModal open={openView} handleClose={handleCloseView} expense={selectedExpense} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {isTablet && <Box>
                {
                    (selectedExpense && openUpdate) ?
                        <UpdateExpenseBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} expense={selectedExpense} />
                        :
                        <AddExpenseBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            width: '100%'
                        }}
                    >

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                        }}>
                            <Box sx={{ px: 2, }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'90%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>

                        <Divider />
                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <UserDropdown
                                isFilter={true}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                            />
                        </Box>

                        {allBranchPermission &&
                            <Box>
                                <Divider />
                                <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                                    <BranchDropdown
                                        isFilter={true}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                    />
                                </Box>
                            </Box>
                        }

                        <Divider />
                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <ExpenseTypeDropdown
                                isFilter={true}
                                selectedExpenseType={selectedExpenseType}
                                setSelectedExpenseType={setSelectedExpenseType}
                            />
                        </Box>

                        <Divider />

                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        <Divider />


                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box width={'80%'}>
                                <Typography variant="body1" sx={{ mx: 1 }}>
                                    {t('totalAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalAmount)}</span>
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" />
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: '20%'
                                }}
                            >
                                <PrintExpenses
                                    data={{
                                        user: selectedUser,
                                        branch: selectedBranch,
                                        expenseType: selectedExpenseType,
                                        name: searchTerm,
                                        time: selectedTime,
                                        expenses: expenses,
                                        deleted: deleted,
                                    }} />
                            </Box>
                        </Box>

                        <Divider />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box sx={{
                                width: '30%',
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                    width: '70%'
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>

                    </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{ px: 2, }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" sx={{ height: '101px' }} />
                        <Box sx={{
                            width: '100%'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50,
                            }}>
                                <Box width={allBranchPermission ? '25%' : '35%'}>
                                    <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={allBranchPermission ? '25%' : '35%'}>
                                    <UserDropdown
                                        isFilter={true}
                                        selectedUser={selectedUser}
                                        setSelectedUser={setSelectedUser}
                                    />
                                </Box>
                                {allBranchPermission && <Divider orientation="vertical" />}
                                {allBranchPermission && <Box width={'25%'}>
                                    <BranchDropdown
                                        isFilter={true}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                    />
                                </Box>}
                                <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                                <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: theme.palette.text.red,
                                        px: 2,
                                        cursor: 'pointer',
                                    }}
                                    onClick={resetFilter}
                                >
                                    <ReplayOutlinedIcon />
                                    <Box width={3}></Box>
                                    <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                                </Box>
                            </Box>

                            <Divider />
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50
                            }}>

                                <Box width={'50%'}>
                                    <TimeDropdown
                                        isFilter={true}
                                        selectedTime={selectedTime}
                                        setSelectedTime={setSelectedTime}
                                    />
                                </Box>

                                <Divider orientation="vertical" />

                                <Box width={'20%'}>
                                    <ExpenseTypeDropdown
                                        isFilter={true}
                                        selectedExpenseType={selectedExpenseType}
                                        setSelectedExpenseType={setSelectedExpenseType}
                                    />
                                </Box>

                                <Divider orientation="vertical" />

                                <Box width={'23%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalAmount)}</span>
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: '7%'
                                    }}
                                >
                                    <PrintExpenses
                                        data={{
                                            user: selectedUser,
                                            branch: selectedBranch,
                                            expenseType: selectedExpenseType,
                                            name: searchTerm,
                                            time: selectedTime,
                                            expenses: expenses,
                                            deleted: deleted,
                                        }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
            }

            <Box sx={{
                display: 'flex'
            }}>

                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedExpense && openUpdate) ?
                            <UpdateExpenseBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} expense={selectedExpense} />
                            :
                            <AddExpenseBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 220px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('user')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('user')}
                                        {sortState.sortBy === 'user' && sortIcon()}
                                    </Box></TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('branch')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('branch')}
                                        {sortState.sortBy === 'branch' && sortIcon()}
                                    </Box></TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('expenseType')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('expenseType')}
                                        {sortState.sortBy === 'expenseType' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('date')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('date')}
                                        {sortState.sortBy === 'date' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('note')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('note')}
                                        {sortState.sortBy === 'note' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {expenses.map((expense, index) => (
                                <TableRow
                                    key={expense.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{expense.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expense.user.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expense.branch.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expense.expenseType.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expense.date}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expense.note}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>

                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.7,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenView(expense)}>
                                                <RemoveRedEyeOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>

                                            <Divider orientation="vertical" flexItem />

                                            {expense.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(expense)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {expense.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(expense)}>
                                                {expense.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>


        </Box>
    );
}

export default Expense;